import type { ComputedRef, Ref } from 'vue'
import type {
  BigFive,
  PersonalityHighlightCaution,
  PersonalityHighlightStrength,
  PersonalityHighlights,
} from '~/types/NotificationsHub'
import type {
  PersonalityAnalysisDto,
  PersonalityCautionPointDto,
  PersonalityStrengthDto,
  PersonalityTraitDto,
} from '~/types/PersonalityAnalysis'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useFrontApi } from '~/composables/useFrontApi'

/**
 * Convertit le snapshot du hub (SignalR `PersonalityHighlightsChanged`) vers le shape de l'analyse REST.
 * @param {PersonalityHighlights} payload - Forces et points de vigilance au format du hub (title/label).
 * @returns {Pick<PersonalityAnalysisDto, 'strengths' | 'cautionPoints'>} Forces et points de vigilance au format DTO.
 */
function toAnalysisHighlights(
  payload: PersonalityHighlights,
): Pick<PersonalityAnalysisDto, 'strengths' | 'cautionPoints'> {
  return {
    strengths: payload.strengths.map(
      (strength: PersonalityHighlightStrength): PersonalityStrengthDto => ({
        name: strength.label,
        description: strength.title,
        score: strength.score,
        level: '',
      }),
    ),
    cautionPoints: payload.cautionPoints.map(
      (cautionPoint: PersonalityHighlightCaution): PersonalityCautionPointDto => ({
        label: cautionPoint.label,
        description: cautionPoint.title,
      }),
    ),
  }
}

export const useAnalysisStore = defineStore('analysis', () => {
  const personalityAnalysis: Ref<PersonalityAnalysisDto | null> = ref(null)
  const bigFive: Ref<BigFive | null> = ref(null)

  const traits: ComputedRef<PersonalityTraitDto[]> = computed(
    (): PersonalityTraitDto[] => personalityAnalysis.value?.traits ?? [],
  )
  const strengths: ComputedRef<PersonalityStrengthDto[]> = computed(
    (): PersonalityStrengthDto[] => personalityAnalysis.value?.strengths ?? [],
  )
  const cautionPoints: ComputedRef<PersonalityCautionPointDto[]> = computed(
    (): PersonalityCautionPointDto[] => personalityAnalysis.value?.cautionPoints ?? [],
  )

  /**
   * Définit le score Big Five (SignalR `BigFiveChanged`).
   * @param {BigFive} payload - Scores des cinq traits (chacun nul tant qu'il n'est pas calculé).
   * @returns {void}
   */
  function setBigFive(payload: BigFive): void {
    bigFive.value = payload
  }

  /**
   * Rafraîchit forces et points de vigilance (SignalR `PersonalityHighlightsChanged`).
   * @param {PersonalityHighlights} payload - Snapshot des forces et points de vigilance au format du hub.
   * @returns {void}
   */
  function setPersonalityHighlights(payload: PersonalityHighlights): void {
    const highlights: Pick<PersonalityAnalysisDto, 'strengths' | 'cautionPoints'> =
      toAnalysisHighlights(payload)
    personalityAnalysis.value = personalityAnalysis.value
      ? { ...personalityAnalysis.value, ...highlights }
      : { traits: null, mbti: null, ...highlights }
  }

  /**
   * Charge l'analyse de personnalité (`GET /api/Profile/personality/analysis`) : traits, MBTI, forces et points de vigilance.
   * @returns {Promise<void>} Promesse résolue après chargement (ou no-op en cas d'erreur).
   */
  async function fetchPersonalityAnalysis(): Promise<void> {
    try {
      const { data } = await useFrontApi().get<PersonalityAnalysisDto>(
        '/api/Profile/personality/analysis',
      )
      personalityAnalysis.value = data
    } catch (error: unknown) {
      console.error('[analysisStore] fetchPersonalityAnalysis:', error)
    }
  }

  return {
    personalityAnalysis,
    bigFive,
    traits,
    strengths,
    cautionPoints,
    setBigFive,
    setPersonalityHighlights,
    fetchPersonalityAnalysis,
  }
})
